import { create } from 'zustand'
import { dbg, dbgEvent } from '../lib/debug'

export interface Message {
  id: string
  sessionId: string
  role: 'user' | 'assistant' | 'system'
  content: string
  contentType: 'text' | 'audio_transcript' | 'file_ref'
  status: 'sending' | 'streaming' | 'complete' | 'error'
  createdAt: Date
}

interface MessageSessionState {
  streamingContent: string
  isStreaming: boolean
}

interface MessagesStoreState {
  sessions: Record<string, MessageSessionState>

  // Actions
  initSession: (sessionId: string) => void
  appendStreamChunk: (sessionId: string, chunk: string) => void
  resetStreaming: (sessionId: string) => void
}

export const useMessagesStore = create<MessagesStoreState>((set) => ({
  sessions: {},

  initSession: (sessionId) =>
    set(state => {
      const isNew = !state.sessions[sessionId]
      dbgEvent('Zustand', 'messages.initSession', { sessionId: sessionId.slice(0, 8), isNew })
      return {
        sessions: {
          ...state.sessions,
          [sessionId]: state.sessions[sessionId] ?? {
            streamingContent: '',
            isStreaming: false,
          },
        },
      }
    }),

  appendStreamChunk: (sessionId, chunk) =>
    set(state => {
      const session = state.sessions[sessionId]
      if (!session) return state
      const newLength = session.streamingContent.length + chunk.length
      dbg('Store', `streamingContent sid=${sessionId.slice(0, 8)}... +${chunk.length} bytes (total: ${newLength})`)
      dbgEvent('Zustand', 'messages.appendStreamChunk', { sessionId: sessionId.slice(0, 8), chunkLen: chunk.length, total: newLength })
      return {
        sessions: {
          ...state.sessions,
          [sessionId]: {
            ...session,
            streamingContent: session.streamingContent + chunk,
            isStreaming: true,
          },
        },
      }
    }),

  resetStreaming: (sessionId) =>
    set(state => {
      const session = state.sessions[sessionId]
      if (!session) return state
      dbgEvent('Zustand', 'messages.resetStreaming', { sessionId: sessionId.slice(0, 8) })
      return {
        sessions: {
          ...state.sessions,
          [sessionId]: {
            ...session,
            isStreaming: false,
            streamingContent: '',
          },
        },
      }
    }),
}))
